/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import LocalizationComponent from '../../../Localization/LocalizationComponent';
import { ccEnum } from '../../CCEnum';
import ComponentBase from '../../ComponentBase';
import CLog, { LogTypes } from '../../ComponentLogger';
import Decorators from '../../Decorators';
import IObservable from '../../Observable/IObservable';
import Observable from '../../Observable/Observable';
import ComponentReference from '../ComponentReference/ComponentReference';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../ComponentReference/ReferenceGroup';
import ObservableComponentBase from './ObservableComponentBase';
import ObservableEvent from './ObservableEvent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Generic pattern for an updateable component that monitors an associated [[ObservableComponentBase]]. */
@cc._decorator.ccclass
export default abstract class UpdateableComponentBase<T extends cc.Component, K> extends ComponentBase
{
    /** The instance ID of the observable to watch. */
    @property({ visible: true })
    protected _observableInstanceID: string = "";
    /** Accessor for the ID of the watched observable. */
    public get ObservableInstanceID(): string { return this._observableInstanceID; }
    public set ObservableInstanceID(observableInstanceID: string)
    {
        this._observableInstanceID = observableInstanceID;
        this.findObservable();
    }

    /** The [[ReferenceGroup]] to search for the target observable. */
    @property({ type: ccEnum.ReferenceGroup, visible: true })
    private _referenceGroup: ReferenceGroup = ReferenceGroup.MainGame;

    /** The watched observable. */
    protected observable: ObservableComponentBase<K> = null;

    /** @hidden */
    private _rootObservable: IObservable<IObservable<K>> = new Observable<IObservable<K>>();

    /** @hidden */
    protected localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();

    /** Convenience accessor for the watched observable */
    public get Observable(): IObservable<K> { return this._rootObservable.Value; }
    /** Mutator for the observable being watched. */
    public set Observable(value: IObservable<K>) { this._rootObservable.Value = value; }

    /** The component to be updated when the observable changes. */
    protected abstract component: T = null;
    /** The update action to execute when the observable changes. */
    @Decorators.BindThis()
    protected abstract updateUI: (event?: ObservableEvent<K>) => void;

    /** Function to execute on IsReadOnly change for [[Observable]] */
    @Decorators.BindThis()
    protected readOnlyChangedHandler: () => void = null;

    /** Whether updateable should trigger update on localization changes. */
    protected bindLocalization: boolean = true;

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        if (this.bindLocalization) { this.localRef.SetContext(LocalizationComponent); }
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        if (!this._observableInstanceID)
        {
            this.enabled = false;
            return;
        }

        if (this.observable === null)
        {
            this.findObservable();
        }

        this.bindListeners();
        this.updateUI();

        if (this.readOnlyChangedHandler)
        {
            this.observable.IsReadOnlyObservable.OnChanged(this.readOnlyChangedHandler);
            this.readOnlyChangedHandler();
        }
    }

    /** @hidden */
    protected findObservable(): void
    {
        const foundObservables: cc.Component[]
            = ComponentReference.GetByInstanceID(this._observableInstanceID, this._referenceGroup);

        if (!foundObservables)
        {
            this.enabled = false;
            return;
        }

        this.observable = foundObservables[0] as ObservableComponentBase<K>;
        this._rootObservable.Value = this.observable;
    }

    /** @hidden */
    protected firstUpdate(dt: number): void
    {
        super.firstUpdate(dt);

        // Force displays to update after initial values set.
        this.valueChangedHandler();
    }

    /** @hidden */
    protected valueChangedHandler = (e: ObservableEvent<K> = null): void =>
    {
        this.updateUI(e);
    }

    /** @hidden */
    protected onDestroy(): void
    {
        if (this._rootObservable && this._rootObservable.HasValue())
        {
            this._rootObservable.OffChanged(this.onDataSourceChanged);
            this._rootObservable.Value.OffChanged(this.valueChangedHandler);
        }

        if (this.bindLocalization) { this.localRef.WithValue(local => local.OffChanged(this.updateUI)); }

        if (this.observable && this.observable.IsReadOnlyObservable)
        {
            this.observable.IsReadOnlyObservable.OffChanged(this.readOnlyChangedHandler);
        }
        super.onDestroy();
    }

    /** @hidden */
    private bindListeners(): void
    {
        if (!this._rootObservable || !this._rootObservable.Value)
        {
            CLog.warn(`Not setup with observable. Disabled.`, LogTypes.Initialization, this);
            this.enabled = false;
            return;
        }

        this._rootObservable.OnChanged(this.onDataSourceChanged);
        this._rootObservable.Value.OnChanged(this.valueChangedHandler);

        if (this.bindLocalization) { this.localRef.WithValue(local => local.OnChanged(this.updateUI)); }

        this.enabled = true;
    }

    /** @hidden */
    private onDataSourceChanged = (): void =>
    {
        // unbind and rebind listeners to new datasource
        this.onDestroy();
        this.bindListeners();
        this.updateUI();
    }
}
